import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import useAsync from 'react-use/esm/useAsync';
import { Content, Page, Header } from '@backstage/core-components';
import { Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, Paper, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { z } from 'zod';
import { SystemId, SystemIdType } from './types';
import { useRequestService } from '../../services/RequestService';
import { AutocompleteInput, SelectInput, SwitchInput } from '../../components/input';
import { AlertMessage } from '../../components/alert';
import { useFormDefinition } from './useFormDefinition';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

const integrationFields = [
  'identityAppCode',
  'racId',
  'smartTalkId',
  'tpdId',
] as const;
type IntegrationFieldType = typeof integrationFields[number];

const systemSchema = z.object({
    domain: z.string().nonempty('Domain is required'),
    subdomain: z.string().optional(),
    namespace: z.string().nonempty('Namespace is required'),
    name: z.string().nonempty('Identifier is required')
        .max(63, 'Identifier must be 63 characters or less')
        .regex(/^[a-zA-Z0-9-]+$/, 'Only letters, numbers, and hyphens are allowed.')
        .refine(val => !/^(supply-|sl-)/i.test(val), {
            message: 'Identifier cannot start with supply- or sl-',
        }),
    title: z.string().nonempty('Name is required'),
    description: z.string().nonempty('Description is required').max(120, 'Description must be 120 characters or less'),
    tags: z.array(z.string()).optional(),
    identityAppCode: z.object({
        kind: z.string().optional(),
        id: z.string().optional(),
    }),
    racId: z.object({
        kind: z.string().optional(),
        id: z.string().optional(),
    }),
    smartTalkId: z.object({
        kind: z.string().optional(),
        id: z.string().optional(),
    }),
    tpdId: z.object({
        kind: z.string().optional(),
        id: z.string().optional(),
    }),
    releaseProcess: z.boolean(),
    useDatabase: z.boolean(),
    migrationProcess: z.boolean(),
    useConfigServer: z.boolean(),
    segmentOrBoard: z.string().nonempty('Segment/Board is required'),
    onboardingType: z.string().optional(),
    marketing: z.string().optional(),
    dataPrivacy: z.string().optional(),
    stakeholder: z.string().nonempty('Stakeholder is required'),
    productOwner: z.string().nonempty('Product owner is required'),
    technicalResponsible: z.string().nonempty('Technical responsible is required'),
    operationsResponsible: z.string().nonempty('Operations responsible is required'),
    securityResponsible: z.string().nonempty('Security responsible is required'),
}).superRefine((data, ctx) => {
    integrationFields.forEach(field => {
        if (data[field] && data[field].kind === 'value' && (!data[field].id || data[field].id?.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field, 'id'],
                message: 'Value is required when providing an integration.',
            });
        }
    });
});

// Valores iniciais simples - definidos diretamente
const initialValues = {
    domain: '',
    subdomain: '',
    namespace: '',
    name: '',
    title: '',
    description: '',
    tags: ['java', 'typescript'] as string[],
    identityAppCode: { kind: 'none' as const, id: '' },
    racId: { kind: 'none' as const, id: '' },
    smartTalkId: { kind: 'none' as const, id: '' },
    tpdId: { kind: 'none' as const, id: '' },
    releaseProcess: true,
    useDatabase: true,
    migrationProcess: true,
    useConfigServer: true,
    segmentOrBoard: '',
    onboardingType: 'namespace' as const,
    marketing: 'no' as const,
    dataPrivacy: 'no' as const,
    stakeholder: '',
    productOwner: '',
    technicalResponsible: '',
    operationsResponsible: '',
    securityResponsible: '',
};

const IntegrationInput = ({
    label,
    identifierName = 'Client Id',
    field,
    formValue,
    onSelectChange,
    onTextChange,
    error = false,
    helperText = ''
}: {
    label: string;
    identifierName?: string;
    field: IntegrationFieldType;
    formValue: SystemId,
    onSelectChange: (field: IntegrationFieldType, value: SystemIdType) => void;
    onTextChange: (field: IntegrationFieldType, value: string) => void;
    error?: boolean;
    helperText?: string;
}) => {
    const [enabled , setEnabled] = useState(false);
    const idOptions = [
        { value: 'none', label: 'None', hidden: true },
        { value: 'request', label: "I don't have a ClientID (it will be requested)" },
        { value: 'value', label: 'I already have a ClientID (provide it on the side)' },
    ];

    return (
        <Paper style={{ padding: 16, marginBottom: 16, width: "100%" }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <SwitchInput
                        label={`Has ${label} integration`}
                        checked={enabled}
                        onChange={(value) => {
                            setEnabled(value);
                            onSelectChange(field, value ? 'request' : 'none');
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6} hidden={!enabled}>
                    <SelectInput
                        label="Type"
                        value={formValue.kind}
                        options={idOptions}
                        onChange={value => onSelectChange(field, value)}
                        disabled={!enabled}
                        variant='standard'
                    />
                </Grid>

                <Grid item xs={12} md={6} hidden={formValue.kind !== 'value'}>
                    <TextField
                        label={`${label} ${identifierName}`}
                        value={formValue.id}
                        onChange={(event) => onTextChange(field, event.target.value)}
                        fullWidth
                        required={formValue.kind === 'value'}
                        error={error}
                        helperText={helperText}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}

export const NewSystemPage = () => {
    const navigate = useNavigate();
    const requestService = useRequestService();
    const catalogApi = useApi(catalogApiRef);

    const {
        formValues,
        setFormValue,
        isFormValid,
        getFieldProps,
        checkNameAvailability
    } = useFormDefinition(systemSchema, initialValues, {
        checkNameAvailability: { fieldName: 'name', requestType: 'product' }
    });

    const { value: users = [] } = useAsync(async () => await requestService.listUsers(), []);
    const { value: domains = [] } = useAsync(async () => await requestService.listDomains('product-area'), []);

    // Filtrar subdomínios baseado no domínio selecionado
    const { value: subdomains = [] } = useAsync(async () => {
        if (!formValues.domain) {
            return [];
        }

        try {
            // Buscar entidades do tipo domain que sejam product-group
            const entities = await catalogApi.getEntities({
                filter: {
                    kind: 'domain',
                    'spec.type': 'product-group'
                }
            });

            // Filtrar apenas os que têm subdomainOf igual ao domínio selecionado
            const filteredSubdomains = entities.items.filter(entity => {
                return entity.spec?.subdomainOf === formValues.domain.split(':').pop();
            });

            return filteredSubdomains.map(entity => ({
                value: stringifyEntityRef(entity),
                label: entity.metadata.title || entity.metadata.name
            }));
        } catch (error) {
            // console.error('Erro ao carregar subdomínios:', error);
            return [];
        }
    }, [formValues.domain]);

    // Resetar subdomain quando domain mudar, apenas se não for mais válido
    useEffect(() => {
        if (formValues.domain && formValues.subdomain) {
            // Verificar se o subdomain atual ainda é válido para o novo domain
            const currentSubdomainValid = subdomains.some(sub => sub.value === formValues.subdomain);
            if (!currentSubdomainValid) {
                setFormValue('subdomain', '');
            }
        }
    }, [formValues.domain, formValues.subdomain, subdomains, setFormValue]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const booleanOptions = [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unknown', label: 'Unknown' },
    ];

    const namespaceOptions = [
        { value: '', label: 'Select a namespace...', hidden: true },
        { value: 'supply-engenharia', label: 'Supply Engenharia' },
        { value: 'supply-logistica', label: 'Supply Logística' },
        { value: 'supply-fabrica', label: 'Supply Fábrica' },
        { value: 'agro-comercializacao', label: 'Agro Comercialização' },
    ];

    const handleChange = (field: string, value: any) => {
        setFormValue(field, value);
    };

    const handleIdTypeChange = (field: IntegrationFieldType, value: SystemIdType) => {
        if (value === 'none') {
            setFormValue(field, { kind: value });
        } else {
            setFormValue(field, { kind: value, id: formValues[field].id });
        }
    };

    const handleIdChange = (field: IntegrationFieldType, value: string) => {
        setFormValue(field, { kind: formValues[field].kind, id: value });
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Converter os valores do formulário para o tipo System esperado pelo serviço
            const systemData = {
                ...formValues,
                // O owner é automaticamente definido como o nome do sistema
                owner: formValues.name,
                // Manter as strings como estão pois o serviço espera strings, não entidades
                domain: formValues.domain,
                // Enviar undefined se subdomain for string vazia
                subdomain: formValues.subdomain || undefined,
                stakeholder: formValues.stakeholder,
                productOwner: formValues.productOwner,
                technicalResponsible: formValues.technicalResponsible,
                operationsResponsible: formValues.operationsResponsible,
                securityResponsible: formValues.securityResponsible,
            };

            await requestService.createSystem(systemData as any);
            navigate('/requests', { state: { message: 'Product request created successfully!' }});
        } catch (err) {
            setError('Failed to submit product request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
            <Page themeId="home">
                <Header title="New Product" type="New request" typeLink='/requests/new-request' />
                <Content>
                    <Card>
                        <CardHeader title="Create a new product" subheader="Fill out the form below to create a new product request. Once submitted, you will be able to track the status of your request on the requests page." />
                        <CardContent>
                            <Divider />
                            <Box
                                component="form"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                style={{ gap: '16px' }}
                                mt={3}
                            >
                                <Box width="100%" maxWidth="800px">
                                    <Box mb={3}>
                                        <Typography variant="h6">Product details</Typography>
                                        <Divider style={{ marginBottom: 12 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <SelectInput
                                                    label="Namespace"
                                                    value={formValues.namespace}
                                                    options={namespaceOptions}
                                                    onChange={value => handleChange('namespace', value)}
                                                    {...getFieldProps('namespace')}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Domain"
                                                    fieldName="domain"
                                                    formValues={formValues}
                                                    options={domains}
                                                    onChange={(field, value) => {
                                                        handleChange(field, value);
                                                        checkNameAvailability(formValues.name, value);
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Subdomain"
                                                    fieldName="subdomain"
                                                    formValues={formValues}
                                                    options={subdomains}
                                                    onChange={handleChange}
                                                    required={false}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Identifier"
                                                    variant="outlined"
                                                    placeholder="e.g. wms, coletaentrega, agro-comercializacao"
                                                    value={formValues.name}
                                                    onChange={(event) => handleChange('name', event.target.value)}
                                                    onBlur={(value) => checkNameAvailability(value.target.value, formValues.domain)}
                                                    required
                                                    fullWidth
                                                    {...getFieldProps('name')}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Display Name"
                                                    variant="outlined"
                                                    placeholder="e.g. Scala - Customizações, Coleta e Entrega, Agro Comercialização"
                                                    value={formValues.title}
                                                    onChange={(event) => handleChange('title', event.target.value)}
                                                    required
                                                    fullWidth
                                                    {...getFieldProps('title')}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Description"
                                                    variant="outlined"
                                                    value={formValues.description}
                                                    onChange={(event) => handleChange('description', event.target.value)}
                                                    required
                                                    fullWidth
                                                    inputProps={{ maxLength: 120 }}
                                                    helperText={`${formValues.description.length}/120`}
                                                    {...getFieldProps('description')}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    options={[]}
                                                    value={formValues.tags}
                                                    onChange={(_, newValue) => handleChange('tags', newValue)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            label="Tags"
                                                            placeholder="e.g: java, typescript (press enter to add)"
                                                            fullWidth
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box mb={3}>
                                        <Typography variant="h6">Integrations</Typography>
                                        <Divider style={{ marginBottom: 12 }} />
                                        <Grid container spacing={2}>
                                            <IntegrationInput
                                                label="Identity"
                                                identifierName="App Code"
                                                field="identityAppCode"
                                                formValue={formValues.identityAppCode}
                                                onSelectChange={handleIdTypeChange}
                                                onTextChange={handleIdChange}
                                                {...getFieldProps('identityAppCode')}
                                            />

                                            <IntegrationInput
                                                label="RAC"
                                                field="racId"
                                                formValue={formValues.racId}
                                                onSelectChange={handleIdTypeChange}
                                                onTextChange={handleIdChange}
                                                {...getFieldProps('racId')}
                                            />

                                            <IntegrationInput
                                                label="SmartTalk"
                                                field="smartTalkId"
                                                formValue={formValues.smartTalkId}
                                                onSelectChange={handleIdTypeChange}
                                                onTextChange={handleIdChange}
                                                {...getFieldProps('smartTalkId')}
                                            />

                                            <IntegrationInput
                                                label="TPD"
                                                field="tpdId"
                                                formValue={formValues.tpdId}
                                                onSelectChange={handleIdTypeChange}
                                                onTextChange={handleIdChange}
                                                {...getFieldProps('tpdId')}
                                            />
                                        </Grid>
                                    </Box>

                                    <Box mb={3}>
                                        <Typography variant="h6">Onboarding TotvsApps</Typography>
                                        <Divider style={{ marginBottom: 12 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    label="Segment/Board"
                                                    variant="outlined"
                                                    value={formValues.segmentOrBoard}
                                                    onChange={(event) => handleChange('segmentOrBoard', event.target.value)}
                                                    required
                                                    fullWidth
                                                    {...getFieldProps('segmentOrBoard')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <SelectInput
                                                    label="Onboarding type"
                                                    value={formValues.onboardingType}
                                                    options={[
                                                        { value: 'team/area', label: 'Team/Area' },
                                                        { value: 'namespace', label: 'Namespace' },
                                                    ]}
                                                    onChange={value => handleChange('onboardingType', value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <SelectInput
                                                    label="Went through marketing analysis"
                                                    value={formValues.marketing}
                                                    options={booleanOptions}
                                                    onChange={value => handleChange('marketing', value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <SelectInput
                                                    label="Aligned with data privacy"
                                                    value={formValues.dataPrivacy}
                                                    options={booleanOptions}
                                                    onChange={value => handleChange('dataPrivacy', value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Stakeholder"
                                                    fieldName="stakeholder"
                                                    formValues={formValues}
                                                    options={users}
                                                    onChange={handleChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Product owner"
                                                    fieldName="productOwner"
                                                    formValues={formValues}
                                                    options={users}
                                                    onChange={handleChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Technical responsible"
                                                    fieldName="technicalResponsible"
                                                    formValues={formValues}
                                                    options={users}
                                                    onChange={handleChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Operations responsible"
                                                    fieldName="operationsResponsible"
                                                    formValues={formValues}
                                                    options={users}
                                                    onChange={handleChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <AutocompleteInput
                                                    label="Security responsible"
                                                    fieldName="securityResponsible"
                                                    formValues={formValues}
                                                    options={users}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box mb={3}>
                                        <Typography variant="h6">Resources</Typography>
                                        <Divider style={{ marginBottom: 12 }} />
                                        <Grid container spacing={2}>
                                            <Grid container>
                                                <Grid item xs={12} md={4}>
                                                    <SwitchInput
                                                        label="Has Database"
                                                        checked={formValues.useDatabase}
                                                        onChange={(value) => handleChange('useDatabase', value)}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <SwitchInput
                                                        label="Has Config Server"
                                                        checked={formValues.useConfigServer}
                                                        onChange={(value) => handleChange('useConfigServer', value)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box mb={3}>
                                        <Typography variant="h6">Deployment</Typography>
                                        <Divider style={{ marginBottom: 12 }} />
                                        <Grid container spacing={2}>
                                            <Grid container>
                                                <Grid item xs={12} md={4}>
                                                    <SwitchInput
                                                        label="Has release process"
                                                        checked={formValues.releaseProcess}
                                                        onChange={(value) => handleChange('releaseProcess', value)}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <SwitchInput
                                                        label="Has migration process"
                                                        checked={formValues.migrationProcess}
                                                        onChange={(value) => handleChange('migrationProcess', value)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" color="primary">
                                    Submit
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Content>

                <Backdrop open={loading} style={{ zIndex: 1300 }}>
                    <CircularProgress color="primary" />
                </Backdrop>

                {error && <AlertMessage message={error} />}
            </Page>
        );
};
